import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListSubheader from "@material-ui/core/ListSubheader";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import * as yup from "yup";
import { useFormik } from "formik";
import { DateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import moment from "moment-timezone";
import uuidv4 from "uuid/v4";
import { useSelector, useDispatch } from "react-redux";
import mixpanel from "mixpanel-browser";

import * as constants from "../../../constants";
import * as actions from "../../../store/actions";
import { createDar } from "../../../utils/appsyncOperations";
import LoadingButton from "../../Buttons/Loading";
import UserSearchDialog from "../UsersSearch";

const initTime = (timezone) => ({
  start: moment().tz(timezone),
  end: moment().tz(timezone),
});

const CreateDar = ({ isOpen, onClose, propertyId }) => {
  const dispatch = useDispatch();
  const property = useSelector((state) => state.properties.byId[propertyId]);
  const usersById = useSelector((state) => state.users.activeById);
  const enqueueSnackbar = useSnackbar().enqueueSnackbar;
  const {
    serviceProviderId,
    place: { coordinates, formattedAddress, timezone },
  } = property;

  const [time, setTime] = useState(() => initTime(timezone));
  const [loading, setLoading] = useState(false);
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [userId, setUserId] = useState(null);
  const [user, setUser] = useState({ displayName: "", userId: "" });
  const [userError, setUserError] = useState(false);
  const [timeError, setTimeError] = useState(null);

  useEffect(() => {
    if (userId) setUser(usersById[userId]);
    else {
      setUser({ displayName: "", userId: "" });
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen) {
      formData.handleReset();
      setTime(() => initTime(timezone));
    }
  }, [isOpen]);

  const formData = useFormik({
    initialValues: {
      note: "",
    },
    onSubmit: (values) => formSubmit(values),
    validationSchema: yup.object({
      note: yup
        .string()
        .max(2000, "Must be 2000 characters or less")
        .required("Required"),
    }),
  });

  const formSubmit = async (values) => {
    if (!user.displayName || !user.userId) {
      return setUserError(true);
    }
    if (time.end.valueOf() < time.start.valueOf()) {
      return setTimeError("End time must be after start time.");
    }
    const darInput = {
      notes: values.note,
      displayName: user.displayName,
      patrolId: uuidv4(),
      senderId: user.userId,
      startCoordinates: coordinates,
      endCoordinates: coordinates,
      sendAlert: true,
      propertyId,
      serviceProviderId,
      formattedAddress,
      startTime: time.start.valueOf(),
      endTime: time.end.valueOf(),
    };

    try {
      setLoading(true);
      const dar = await createDar(darInput);
      mixpanel.track(constants.mixpanel.PATROL_CREATE);
      dispatch(actions.dars.setDar(dar));
      onClose();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error, { variant: "error" });
    }

    setLoading(false);
  };

  const noteError = formData.touched.note && formData.errors.note;

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Create Daily Activity Report</DialogTitle>
        <DialogContent>
          <List>
            <ListSubheader disableSticky>Duration</ListSubheader>
            {
              <ListItem>
                <Grid container spacing={2}>
                  <MuiPickersUtilsProvider utils={MomentUtils}>
                    <Grid item xs={6}>
                      <DateTimePicker
                        ampm={false}
                        format={constants.moment.format}
                        label="Started At"
                        inputVariant="filled"
                        value={time.start}
                        onChange={(start) => {
                          if (!start) return;
                          setTime({ ...time, start });
                          setTimeError(null);
                        }}
                        error={timeError}
                        helperText={timeError}
                        disableFuture
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <DateTimePicker
                        ampm={false}
                        format={constants.moment.format}
                        label="Ended At"
                        inputVariant="filled"
                        value={time.end}
                        onChange={(end) => {
                          if (!end) return;
                          setTime({ ...time, end });
                          setTimeError(null);
                        }}
                        error={timeError}
                        helperText={timeError}
                        fullWidth
                        disableFuture
                      />
                    </Grid>
                  </MuiPickersUtilsProvider>
                </Grid>
              </ListItem>
            }
            <ListSubheader disableSticky>User</ListSubheader>
            <ListItem>
              <TextField
                fullWidth
                required
                onClick={() => setShowUserSearch(true)}
                variant={"filled"}
                value={user.displayName}
                error={userError}
                onChange={() => setUserError(false)}
                helperText={userError ? "Required" : null}
                label={"User"}
              />
            </ListItem>
            <ListSubheader disableSticky>Note</ListSubheader>
            <ListItem>
              <TextField
                fullWidth
                required
                id={"note"}
                variant={"filled"}
                onBlur={formData.onBlur}
                value={formData.values.note}
                onChange={formData.handleChange}
                error={noteError}
                helperText={noteError ? formData.errors.note : null}
                label={"Note"}
              />
            </ListItem>
          </List>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            onClick={formData.handleSubmit}
            color="primary"
            isLoading={loading}
          >
            Create
          </LoadingButton>
          <Button onClick={onClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <UserSearchDialog
        open={showUserSearch}
        onClose={() => setShowUserSearch(false)}
        onUserClick={(id) => {
          setUserError(false);
          setUserId(id);
          setShowUserSearch(false);
        }}
        showHours={false}
        users={Object.values(usersById)}
      />
    </>
  );
};

export default CreateDar;
